import classnames from 'classnames';
import React from 'react';
import { Icon, Select } from 'grab-ui';
import { memoizeObject } from 'helper/memoize-one';
import { isEmpty, isString } from 'common/utils/data-utils';
import pinAddressSvg from 'public/images/pin-address.svg';
import css from './address-field.less';

const DOODLE_VALUE = 'doodle-value';
const Option = Select.Option;

const checkInvalidCountry = (currentCountryID, countryID) =>
  !(currentCountryID === countryID);

const renderAddressOption = address => (
  <div className={css.addressOptionContent}>
    <h5 className={css.addressOptionInfo}>{address.name}</h5>
    <h6 className={css.addressOptionSubInfo}>{address.combined_address}</h6>
  </div>
);

const renderAddressLabel = address => (
  <div className={css.addressLabel}>
    {address.name} • {address.combined_address}
  </div>
);

const renderSubAddressOption = (address, parentAddress) => {
  const {
    address: childAddress,
    distance_between: distanceBetween,
    tips,
  } = address;

  return (
    <div
      className={classnames(
        css.addressOptionContent,
        css.addressOptionDropdownContent,
      )}
    >
      <h5 className={css.addressOptionInfo}>{childAddress.name}</h5>
      {distanceBetween && (
        <h6 className={css.addressOptionSubInfo}>
          {distanceBetween} km
          {tips && ` • ${tips}`}
        </h6>
      )}
    </div>
  );
};

export const loadingOption = (
  <Option key={DOODLE_VALUE} disabled>
    <Icon type="loading" />
  </Option>
);

const renderSubAddressLabel = (address, parentAddress) => {
  const { address: childAddress } = address;

  return (
    <div className={css.addressLabel}>
      {childAddress.name} • {parentAddress.name}
    </div>
  );
};

const buildOptions = (result, extra, pois, city, isPickupAddress) => {
  const { countryID } = city || {};

  const extraMap = {};
  if (extra) {
    extra.forEach(address => {
      extraMap[address.id] = address;
    });
  }

  const poiMap = {};
  const existedPoi = {};
  pois.forEach(poi => {
    const { id, root, pick } = poi;

    existedPoi[id] = id;
    existedPoi[root] = root;
    existedPoi[pick] = pick;
    if (!root) {
      poiMap[id] = id;
    } else if (!poiMap[root]) {
      poiMap[root] = [{ id, pick }];
    } else {
      poiMap[root].push({ id, pick });
    }
  });

  const resultMap = {};
  if (result) {
    result.forEach(address => {
      resultMap[address.id] = address;
      if (!existedPoi[address.id]) {
        poiMap[address.id] = address.id;
      }
    });
  }

  const addressOptions = [];
  const addressMap = {};
  Object.keys(poiMap).forEach(poi => {
    if (!resultMap[poi] && !extraMap[poi]) return;

    const isRootPoi = !isString(poiMap[poi]);
    const currentItem = resultMap[poi] || extraMap[poi];
    const { address, latlng } = currentItem;

    const key = JSON.stringify({
      latitude: latlng.latitude,
      longitude: latlng.longitude,
    });
    if (countryID && checkInvalidCountry(countryID, address.country_id)) return;

    if (addressMap[key]) return;

    addressMap[key] = currentItem;
    addressOptions.push(
      <Option
        key={key}
        className={css.addressOption}
        disabled={isRootPoi}
        label={renderAddressLabel(address)}
      >
        {renderAddressOption(address)}
      </Option>,
    );

    if (!isRootPoi) return;

    poiMap[poi].forEach(({ id, pick }) => {
      const childPoiAddress =
        !resultMap[id] && !extraMap[id]
          ? resultMap[pick] || extraMap[pick]
          : resultMap[id] || extraMap[id];
      if (!childPoiAddress) return;

      const { latlng: childLatlng } = childPoiAddress;
      const ChildPoiIcon = isPickupAddress ? (
        <div className={css.addressOptionPickupIcon} />
      ) : (
        <img src={pinAddressSvg} alt="" />
      );
      const childKey = JSON.stringify({
        latitude: childLatlng.latitude,
        longitude: childLatlng.longitude,
      });

      if (addressMap[childKey]) return;

      addressMap[childKey] = childPoiAddress;
      addressOptions.push(
        <Option
          key={childKey}
          className={classnames(css.addressOption, css.addressOptionDropdown)}
          label={renderSubAddressLabel(childPoiAddress, address)}
        >
          {ChildPoiIcon}
          {renderSubAddressOption(childPoiAddress, address)}
        </Option>,
      );
    });
  });

  return { addressOptions, addressMap };
};

export const reverseSearchOptionsBuilder = memoizeObject(
  (data, city, isPickupAddress) => {
    if (isEmpty(data) || !data.poi || !(data.result || data.extra)) {
      return { addressOptions: [], addressMap: {} };
    }
    const result = Array.isArray(data.result) ? data.result : [data.result];
    const extra = Array.isArray(data.extra) ? data.extra : [data.extra || {}];
    const pois = Array.isArray(data.poi) ? data.poi : [data.poi];

    return buildOptions(result, extra, pois, city, isPickupAddress);
  },
);

export const keywordSearchOptionsBuilder = memoizeObject(
  (data, city, isPickupAddress) => {
    if (isEmpty(data) || !data.pois || !(data.result || data.extra)) {
      return { addressOptions: [], addressMap: {} };
    }
    const result = data.result;
    const extra = data.extra;
    const pois = data.pois;

    return buildOptions(result, extra, pois, city, isPickupAddress);
  },
);
